'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import If from '@/app/components/If'
import Button from '@/app/components/ui/Button'
import CustomCheckbox from '@/app/components/ui/Checkbox'
import CodeVerification from '@/app/components/ui/CodeVerification'
import Input from '@/app/components/ui/Input'
import Timer from '@/app/components/ui/Timer'
import Typography from '@/app/components/ui/Typograph'
import styles from '@/app/styles/pages/sign-in.module.scss'

const recoverPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Campo obrigatório')
    .refine(value => /\S+@\S+\.\S+/.test(value), {
      message: 'Deve ser um email válido',
    }),
})

type ForgotPasswordInputs = z.infer<typeof recoverPasswordSchema>

export default function ForgotPassword() {
  const [sentCode, setSentCode] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInputs>({
    resolver: zodResolver(recoverPasswordSchema),
  })

  const emailValue = watch('email')

  const onSubmit: SubmitHandler<ForgotPasswordInputs> = async data => {
    try {
      const response = await fetch('../../api/recover-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email }),
      })
      if (response.ok) {
        setSentCode(true)
      } else {
        const body = await response.json()
        toast.error(body.error || 'Erro ao enviar o código')
      }
    } catch (err) {
      toast.error('Erro de rede ao enviar o código')
      console.error(err)
    }
  }

  return (
    <div className={styles.loginContainer}>
      <div className="flex flex-col flex-1 pt-10">
        <div className={styles.logo}>
          <Image
            src="/assets/logos/logo-branco-simples.svg"
            alt="Logo"
            width={100}
            height={100}
            priority
          />
        </div>

        <If cond={!sentCode}>
          <div className={styles.loginForm}>
            <h1 className={styles.title}>Recuperação de senha</h1>

            <div className="mt-6">
              <form onSubmit={handleSubmit(onSubmit)}>
                <Input
                  label="Email"
                  type="email"
                  id="email"
                  placeholder="Endereço de e-mail"
                  variant="outlined"
                  {...register('email')}
                  error={errors.email?.message}
                  className="mb-4"
                />

                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  className="mt-6 mb-6 w-full"
                >
                  <span>Enviar e-mail de recuperação</span>
                </Button>
                <div className="flex justify-center">
                  <Link href="/sign-in">
                    <Button type="button" variant="simple">
                      <span>Voltar para o login</span>
                    </Button>
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </If>

        <If cond={sentCode}>
          <div className="w-full mx-auto max-w-full mt-2 z-10">
            <Typography variant="h6">
              Enviamos um e-mail de recuperação para{' '}
              <span className="font-semibold">{emailValue}</span>
            </Typography>
          </div>
        </If>
      </div>
    </div>
  )
}
